import { type ReactNode, useRef, useState } from 'react'
import { isNotEmpty, type CardUpdateInput, type CompanyData, type DecoratedCompanyCard } from '@terros/common'
import { showNotification } from '@mantine/notifications'
import { useDisclosure } from '@mantine/hooks'
import { useForm } from '@mantine/form'
import { Badge, Container, Divider, Flex, Modal, ScrollArea, Stack } from '@mantine/core'
import { useToastError } from '@hooks'
import { useTranslation, useUpdateCard } from '@front/model'
import { ButtonSave, Page } from '@components'
import {
  getInitialReviewRoutingValues,
  getReviewRoutingErrors,
  getSavableTeamInboxEmail,
  getSavedReviewRoutingValues,
} from './reviewRouting'
import { type PreviewLayout } from './preview/previewContent'
import { getMyCardValidationErrors } from './myCardValidation'
import { DEFAULT_MESSAGE_BODY } from './components/MessageTemplateFields'
import {
  CompanyCardFields,
  CompanyCardFormValuesSchema,
  type CompanyCardFormValues,
} from './components/CompanyCardFields'
import { isDefaultCompanyLink, toSuggestedSlug, withDefaultCompanyLinks } from './components/BrandingFields'
import { CardLivePreview } from './CardLivePreview'

type Props = {
  company: CompanyData
  existingCard: DecoratedCompanyCard | undefined
  repAvatarUrl: string
}

export const MyCardForm = ({ company, existingCard, repAvatarUrl }: Props): ReactNode => {
  const { update, isUpdating, error } = useUpdateCard()
  const { t } = useTranslation()
  const [isPreviewExpanded, { close: closePreview, open: openPreview }] = useDisclosure(false)
  const [previewLayout, setPreviewLayout] = useState<PreviewLayout>('mobile')
  const lastSavedInboxEmail = useRef(existingCard?.teamInboxEmail)
  const initialReviewRouting = getInitialReviewRoutingValues(existingCard)
  const initialSections = existingCard?.sections ?? {
    trust: true,
    gallery: true,
    reviews: true,
    cta: true,
    socials: true,
  }
  useToastError(error)

  const form = useForm<CompanyCardFormValues>({
    initialValues: {
      slug: existingCard?.slug ?? toSuggestedSlug(company.name),
      links: withDefaultCompanyLinks(existingCard?.links ?? [], !existingCard),
      attachmentIds: existingCard?.attachmentIds ?? [],
      theme: existingCard?.theme ?? {},
      sections: initialSections,
      imageSource: existingCard?.imageSource === 'rep' ? 'both' : (existingCard?.imageSource ?? 'company'),
      maxImagesPerCard: existingCard?.maxImagesPerCard,
      ...initialReviewRouting,
      notifyManager: existingCard?.notifyManager,
      messageTemplates: existingCard?.messageTemplates ?? [
        { name: 'Default', tag: 'default', body: DEFAULT_MESSAGE_BODY },
      ],
      placeId: existingCard?.placeId,
    },
    initialErrors: getReviewRoutingErrors(
      { ...initialReviewRouting, sections: initialSections, placeId: existingCard?.placeId },
      t
    ),
    validate: (values) => getMyCardValidationErrors(values, t),
    validateInputOnBlur: ['teamInboxEmail'],
  })

  const submit = async (values: CompanyCardFormValues): Promise<void> => {
    const savableValues = {
      ...values,
      teamInboxEmail: getSavableTeamInboxEmail(values, lastSavedInboxEmail.current),
    }
    const cardValues = CompanyCardFormValuesSchema.parse(savableValues)
    const populatedLinks = cardValues.links?.filter((link) => isNotEmpty(link.url)) ?? []
    const links = cardValues.links?.filter((link) => isNotEmpty(link.url) || isDefaultCompanyLink(link))
    const savedReviewRouting = getSavedReviewRoutingValues({
      values: { ...cardValues, feedbackInboxEnabled: values.feedbackInboxEnabled },
      savedThreshold: existingCard?.starThreshold,
      showSocials: isNotEmpty(populatedLinks),
    })
    const updatedCard: CardUpdateInput['card'] = {
      ...cardValues,
      links,
      ...savedReviewRouting,
      entityType: 'company',
      entityId: company.companyId,
    }
    await update(updatedCard)
    lastSavedInboxEmail.current = cardValues.teamInboxEmail
    form.setValues(savableValues)
    form.resetDirty(savableValues)
    showNotification({ message: t('MyCard saved'), color: 'green' })
  }

  return (
    <form onSubmit={form.onSubmit(submit)}>
      <Page>
        <Page.Heading title={t('MyCard')}>
          <Badge color={existingCard ? 'green' : 'gray'} variant='light'>
            {existingCard ? t('Live') : t('Not published')}
          </Badge>
          <ButtonSave disabled={!form.isDirty() || !form.isValid()} loading={isUpdating} type='submit' />
        </Page.Heading>
        <Page.Content noPadding>
          <Flex flex={1} mih={0}>
            <ScrollArea flex={1} mih={0} miw={0} type='auto'>
              <Stack maw={720} mx='auto' p='xl' w='100%'>
                <CompanyCardFields company={company} form={form} placeLabel={existingCard?.placeLabel} />
              </Stack>
            </ScrollArea>
            <Divider orientation='vertical' />
            <ScrollArea mih={0} miw={360} type='auto' w={360}>
              <Stack p='xl'>
                {!isPreviewExpanded && (
                  <CardLivePreview
                    companyName={company.name}
                    card={form.values}
                    gallerySlug={existingCard?.slug}
                    layout={previewLayout}
                    repAvatarUrl={repAvatarUrl}
                    onExpand={openPreview}
                    onLayoutChange={setPreviewLayout}
                  />
                )}
              </Stack>
            </ScrollArea>
          </Flex>
        </Page.Content>
      </Page>
      <Modal fullScreen opened={isPreviewExpanded} title={t('MyCard preview')} onClose={closePreview}>
        <Container size='lg'>
          <Stack>
            <CardLivePreview
              companyName={company.name}
              card={form.values}
              gallerySlug={existingCard?.slug}
              layout={previewLayout}
              repAvatarUrl={repAvatarUrl}
              onLayoutChange={setPreviewLayout}
            />
          </Stack>
        </Container>
      </Modal>
    </form>
  )
}
